import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNode, useEditor, useEditingCond } from '@splashthat/splash-cms-engine';
import {
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
    BRAND_LIBRARY_BUTTON_HOVER_COLOR,
    BRAND_LIBRARY_BUTTON_TEXT,
} from 'common/constants/brandLibrary';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';
import { softSanitizeUrl } from 'common/components/TouchpointEditor/utils';

import { NODE_ID_VIRTUAL_CONTENT } from '../../../constants';
import { LinkOutContainer, LinkOutHeadline, LinkOutButton } from './LinkOut.styles';

const BASE_CLASS_NAME = 'LinkOut';

export default function LinkOut({ styles, descendants, selected, hovered }) {
    const node = useNode();
    const editor = useEditor((state) => ({
        editing: state.editing,
        nodes: {
            [NODE_ID_VIRTUAL_CONTENT]: state.nodes[NODE_ID_VIRTUAL_CONTENT],
        },
    }));
    const { theme } = useTouchpointData();
    const isVirtualContentTypeLinkOut =
        editor.nodes?.[NODE_ID_VIRTUAL_CONTENT]?.meta?.virtualContentType === 'link_out';
    const onClickButton = useEditingCond((e) => e.preventDefault());
    const setContent = useCallback((descendantId, text) =>
        node.actions.setProp((editedNode) => {
            editedNode.descendants[descendantId].meta.content = text; // eslint-disable-line no-param-reassign
            return editedNode;
        }),
    );

    if (!isVirtualContentTypeLinkOut) {
        return null;
    }

    return (
        <LinkOutContainer
            onMouseLeave={node.actions.onMouseLeave()}
            onMouseMove={node.actions.onMouseMove()}
            data-qa={`${BASE_CLASS_NAME}--container`}
            onClick={node.actions.onClick()}
            selected={selected}
            hovered={hovered}
            $backgroundColor={theme.colors[styles.backgroundColor]}
            $backgroundImageUrl={styles.backgroundImageUrl}
        >
            <LinkOutHeadline
                $fontFamily={theme.activeFonts[descendants.headline.styles.fontFamily]}
                $fontSize={descendants.headline.styles.fontSize}
                $fontColor={theme.colors[descendants.headline.styles.fontColor]}
                selected={selected}
                hovered={hovered}
            >
                <TextEditable setContent={(text) => setContent('headline', text)} disabled={!editor.editing}>
                    {descendants.headline.meta.content}
                </TextEditable>
            </LinkOutHeadline>
            <LinkOutButton
                $backgroundColor={theme.colors[descendants.button.styles.backgroundColor]}
                $backgroundColorHover={theme.colors[descendants.button.styles.backgroundColorHover]}
                $fontColor={theme.colors[descendants.button.styles.fontColor]}
                $fontColorHover={theme.colors[descendants.button.styles.fontColorHover]}
                $fontSize={descendants.button.styles.fontSize}
                $fontFamily={theme.activeFonts[descendants.button.styles.fontFamily]}
                $borderColor={theme.colors[descendants.button.styles.borderColor]}
                $borderColorHover={theme.colors[descendants.button.styles.borderColorHover]}
                $borderWidth={descendants.button.styles.borderWidth}
                $borderRadius={descendants.button.styles.borderRadius}
                onClick={onClickButton}
                selected={selected}
                hovered={hovered}
                href={softSanitizeUrl(descendants.button.meta.href) || null}
            >
                <TextEditable setContent={(text) => setContent('button', text)} disabled={!editor.editing}>
                    {descendants.button.meta.content}
                </TextEditable>
            </LinkOutButton>
        </LinkOutContainer>
    );
}

LinkOut.splash = {
    related: {
        settings: React.lazy(() => import('./LinkOut.tools')),
    },
    defaultProps: {
        styles: {
            backgroundColor: '',
            backgroundImageUrl: '',
        },
        meta: {
            accessibilityText: '',
        },
        descendants: {
            headline: {
                styles: {
                    fontFamily: 0,
                    fontSize: 24,
                    fontColor: 1,
                },
                meta: {
                    content: 'Join us!',
                },
            },
            button: {
                styles: {
                    backgroundColor: BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
                    backgroundColorHover: BRAND_LIBRARY_BUTTON_HOVER_COLOR,
                    fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                    fontColorHover: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                    fontSize: 16,
                    fontFamily: BRAND_LIBRARY_BUTTON_TEXT,
                    borderColor: BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
                    borderColorHover: BRAND_LIBRARY_BUTTON_HOVER_COLOR,
                    borderWidth: {
                        top: 1,
                        bottom: 1,
                        left: 1,
                        right: 1,
                    },
                    borderRadius: 4,
                },
                meta: {
                    content: 'Join Now',
                    href: '',
                },
            },
        },
    },
};

LinkOut.propTypes = {
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    styles: PropTypes.shape({
        backgroundColor: PropTypes.number,
    }).isRequired,
    descendants: PropTypes.shape({
        headline: PropTypes.shape({
            styles: PropTypes.shape({
                fontFamily: PropTypes.number,
                fontSize: PropTypes.number,
                fontColor: PropTypes.number,
            }).isRequired,
            meta: PropTypes.shape({
                content: PropTypes.string.isRequired,
            }).isRequired,
        }),
        button: PropTypes.shape({
            styles: PropTypes.shape({
                backgroundColor: PropTypes.number,
                backgroundColorHover: PropTypes.number,
                fontColor: PropTypes.number,
                fontColorHover: PropTypes.number,
                fontSize: PropTypes.number,
                fontFamily: PropTypes.number,
                borderColor: PropTypes.number,
                borderColorHover: PropTypes.number,
                borderStyle: PropTypes.string,
                borderWidth: PropTypes.object,
                borderRadius: PropTypes.number,
            }).isRequired,
            meta: PropTypes.shape({
                content: PropTypes.string.isRequired,
                href: PropTypes.string,
            }).isRequired,
        }),
    }).isRequired,
};
